import { useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { Download, FileText, Loader2, Lock } from "lucide-react";

import { loadPublicAsset, type PublicAsset } from "@/lib/assets-api";

type AssetSearch = { token?: string };

export const Route = createFileRoute("/$tenantSlug/assets/$assetSlug")({
  component: AssetPage,
  validateSearch: (search): AssetSearch => ({
    token: typeof search.token === "string" && search.token ? search.token : undefined,
  }),
});

function AssetPage() {
  const { tenantSlug, assetSlug } = Route.useParams();
  const { token } = Route.useSearch();
  const [submissionToken, setSubmissionToken] = useState<string | undefined>(token);

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ["public-asset", tenantSlug, assetSlug, submissionToken ?? ""] as const,
    queryFn: () => loadPublicAsset(tenantSlug, assetSlug, submissionToken),
  });

  if (isLoading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <Loader2 className="size-5 animate-spin text-slate-400" />
      </div>
    );
  }
  if (isError || !data) {
    return (
      <div className="flex-1 flex items-center justify-center p-8 text-center">
        <div className="max-w-md space-y-2">
          <h1 className="text-lg font-medium">Asset unavailable</h1>
          <p className="text-sm text-slate-500">
            {(error as Error | null)?.message ?? "This asset may have been removed."}
          </p>
        </div>
      </div>
    );
  }

  const gated = !!data.gated_form_slug && !data.download_url;

  return (
    <div className="flex-1 p-4 sm:p-8 bg-slate-50 dark:bg-slate-950">
      <article className="max-w-2xl mx-auto bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl p-6 sm:p-10 space-y-5">
        <AssetHeader asset={data} />
        {gated ? (
          <GatedDownloadPanel
            tenantSlug={tenantSlug}
            assetSlug={assetSlug}
            formSlug={data.gated_form_slug!}
            formName={data.gated_form_name}
            onTokenIssued={(t) => {
              setSubmissionToken(t);
              void refetch();
            }}
          />
        ) : data.download_url ? (
          <a
            href={data.download_url}
            className="inline-flex items-center gap-2 rounded-md bg-slate-900 dark:bg-slate-100 text-white dark:text-slate-900 px-4 py-2.5 text-sm font-medium hover:opacity-90 transition"
            download
          >
            <Download className="size-4" /> Download
          </a>
        ) : (
          <p className="text-sm text-slate-500">
            This asset is temporarily unavailable. Please try again later.
          </p>
        )}
      </article>
      <p className="text-xs text-slate-400 text-center pt-4">Powered by ActionaryCRM</p>
    </div>
  );
}

function AssetHeader({ asset }: { asset: PublicAsset }) {
  const meta = asset.metadata as { thumbnail_url?: string; description?: string };
  const sizeKb = asset.byte_size ? Math.round(asset.byte_size / 1024) : null;
  return (
    <header className="space-y-3">
      {meta.thumbnail_url ? (
        <img src={meta.thumbnail_url} alt="" className="w-full h-40 object-cover rounded-md" />
      ) : (
        <div className="rounded-md bg-slate-100 dark:bg-slate-800 p-6 inline-flex items-center justify-center">
          <FileText className="size-6 text-slate-400" />
        </div>
      )}
      <div className="space-y-1">
        <h1 className="text-2xl font-medium">{asset.title || asset.slug}</h1>
        {meta.description ? (
          <p className="text-sm text-slate-500 whitespace-pre-line">{meta.description}</p>
        ) : null}
        <p className="text-xs text-slate-400">
          {asset.mime_type ?? "file"}
          {sizeKb != null ? ` · ${sizeKb} KB` : ""}
        </p>
      </div>
    </header>
  );
}

function GatedDownloadPanel({
  tenantSlug,
  assetSlug,
  formSlug,
  formName,
  onTokenIssued: _onTokenIssued,
}: {
  tenantSlug: string;
  assetSlug: string;
  formSlug: string;
  formName: string | null;
  onTokenIssued: (token: string) => void;
}) {
  return (
    <div className="rounded-md border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900 p-4 space-y-3">
      <div className="flex items-center gap-2 text-sm font-medium">
        <Lock className="size-4 text-slate-500" />
        Complete the form below to unlock the download
      </div>
      <p className="text-xs text-slate-500">
        {formName
          ? `Submit the "${formName}" form and we'll send you the download link, plus unlock it on this page.`
          : "Submit the form and we'll unlock the download."}
      </p>
      <Link
        to="/$tenantSlug/forms/$formSlug"
        params={{ tenantSlug, formSlug }}
        search={{ return_to_asset: assetSlug } as never}
        className="inline-flex items-center gap-2 rounded-md bg-slate-900 dark:bg-slate-100 text-white dark:text-slate-900 px-4 py-2 text-sm font-medium hover:opacity-90 transition"
      >
        Open form
      </Link>
    </div>
  );
}
